<template>
  <div class="settings-view">
    <PageHeader title="Roles" subtitle="Define permission sets for the workspace and projects." />
    <div v-if="message" class="workspace-alert">{{ message }}</div>
    <AsyncState :loading="!roles">
      <div class="roles-shell">
        <aside class="role-list">
          <div class="role-list-head">
            <span>Roles</span>
            <button type="button" class="role-list-new" @click="startCreate">
              <Plus class="icon" /> New
            </button>
          </div>
          <template v-for="group in roleGroups" :key="group.scope">
            <div v-if="group.roles.length" class="role-list-group">{{ group.label }}</div>
            <button
              v-for="role in group.roles"
              :key="role.id"
              type="button"
              class="role-list-item"
              :class="{ active: role.id === selectedRoleId }"
              @click="selectRole(role.id)"
            >
              <ShieldCheck v-if="role.scope === RoleScope.GLOBAL" class="icon" />
              <FolderClosed v-else class="icon" />
              <span class="role-list-name">{{ role.name }}</span>
              <span v-if="role.builtInKey" class="status-pill">Built-in</span>
              <span v-else-if="roleDirtyCount(role.id) > 0" class="role-list-dot" title="Unsaved changes"></span>
            </button>
          </template>
          <div v-if="creating" class="role-create">
            <input ref="createInput" v-model="newRoleName" placeholder="Role name" @keyup.enter="createRole" />
            <select v-model="newRoleScope">
              <option :value="RoleScope.GLOBAL">Global</option>
              <option :value="RoleScope.PROJECT">Project</option>
            </select>
            <div class="role-create-actions">
              <button type="button" class="btn" @click="cancelCreate">Cancel</button>
              <button type="button" class="btn btn-primary" @click="createRole">Create</button>
            </div>
          </div>
        </aside>

        <section v-if="selectedRole" class="role-detail">
          <div class="role-detail-head">
            <h2>{{ selectedRole.name }}</h2>
            <span class="status-pill">{{ selectedRole.scope }}</span>
            <span v-if="selectedRole.builtInKey" class="status-pill success">Built-in</span>
          </div>
          <div class="role-detail-body">
            <div v-if="isReadOnly" class="role-readonly-note">
              <Lock class="icon" />
              <span>Built-in roles can't be edited.</span>
            </div>
            <div v-for="group in permissionGroups(selectedRole.scope)" :key="group.group" class="perm-group">
              <div class="perm-group-head">
                <span>{{ group.group }}</span>
                <button
                  v-if="!isReadOnly"
                  type="button"
                  class="perm-group-all"
                  @click="toggleGroup(selectedRole!.id, group)"
                >
                  {{ groupAllSelected(selectedRole!.id, group) ? 'Clear all' : 'Select all' }}
                </button>
              </div>
              <div v-for="def in group.items" :key="def.permission" class="perm-row">
                <div class="perm-row-label">{{ def.label }}</div>
                <ToggleSwitch
                  :model-value="hasPermission(selectedRole!.id, def.permission)"
                  :disabled="isReadOnly"
                  @update:model-value="value => setPermission(selectedRole!.id, def.permission, value)"
                />
              </div>
            </div>
          </div>
          <div v-if="!isReadOnly" class="settings-save-bar">
            <span class="settings-save-hint" :class="{ dirty: dirtyCount > 0 }">
              {{ dirtyCount > 0 ? `${dirtyCount} unsaved change${dirtyCount > 1 ? 's' : ''}` : 'All changes saved' }}
            </span>
            <button type="button" class="btn" :disabled="dirtyCount === 0" @click="discardChanges">Discard</button>
            <button type="button" class="btn btn-primary" :disabled="dirtyCount === 0" @click="saveRole">Save changes</button>
          </div>
        </section>
      </div>
    </AsyncState>
  </div>
</template>

<script setup lang="ts">
import { FolderClosed, Lock, Plus, ShieldCheck } from 'lucide-vue-next';
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import type { EntityId } from '../api/entityId';
import {
  BuiltInRoleKey,
  CommandsService,
  PermissionKind,
  RoleScope,
  type GetRolesQueryResponse,
  type PermissionDefinitionResponse
} from '../api/generated';
import AsyncState from '../components/settings/AsyncState.vue';
import PageHeader from '../components/settings/PageHeader.vue';
import ToggleSwitch from '../components/settings/ToggleSwitch.vue';
import { getErrorMessage, useSessionStore } from '../stores/session';

type RoleSummary = NonNullable<GetRolesQueryResponse['roles']>[number];
interface PermissionGroup {
  group: string;
  items: PermissionDefinitionResponse[];
}

const selectedPermissions = reactive<Record<EntityId, Set<PermissionKind>>>({});
const originalPermissions = reactive<Record<EntityId, Set<PermissionKind>>>({});
const session = useSessionStore();
const roles = ref<GetRolesQueryResponse | null>(null);
const selectedRoleId = ref<EntityId | null>(null);
const creating = ref(false);
const newRoleName = ref('');
const newRoleScope = ref<RoleScope>(RoleScope.PROJECT);
const createInput = ref<HTMLInputElement | null>(null);
const message = ref<string | null>(null);

const roleGroups = computed(() => [
  { scope: RoleScope.GLOBAL, label: 'Global', roles: rolesForScope(RoleScope.GLOBAL) },
  { scope: RoleScope.PROJECT, label: 'Project', roles: rolesForScope(RoleScope.PROJECT) }
]);
const selectedRole = computed(() => roles.value?.roles.find(role => role.id === selectedRoleId.value) ?? null);
const isReadOnly = computed(() => selectedRole.value?.builtInKey != null);
const dirtyCount = computed(() => (selectedRoleId.value ? roleDirtyCount(selectedRoleId.value) : 0));

onMounted(load);

async function load(): Promise<void> {
  try {
    applyRoles(session.pageBootstrap?.roles ?? null);
    message.value = null;
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to load roles.');
  }
}

function applyRoles(response: GetRolesQueryResponse | null): void {
  if (response == null) {
    throw new Error('Roles bootstrap payload was missing.');
  }

  roles.value = response;
  for (const key of Object.keys(selectedPermissions)) {
    delete selectedPermissions[key];
  }

  for (const key of Object.keys(originalPermissions)) {
    delete originalPermissions[key];
  }

  for (const role of response.roles) {
    selectedPermissions[role.id] = new Set(role.permissions ?? []);
    originalPermissions[role.id] = new Set(role.permissions ?? []);
  }

  if (selectedRoleId.value == null || !response.roles.some(role => role.id === selectedRoleId.value)) {
    selectedRoleId.value = response.roles[0]?.id ?? null;
  }
}

function rolesForScope(scope: RoleScope): RoleSummary[] {
  return (roles.value?.roles ?? []).filter(role => role.scope === scope);
}

function selectRole(roleId: EntityId): void {
  selectedRoleId.value = roleId;
}

function permissionGroups(scope: RoleScope): PermissionGroup[] {
  const groups = new Map<string, PermissionDefinitionResponse[]>();
  for (const definition of roles.value?.permissionDefinitions ?? []) {
    if (definition.roleScope !== scope) {
      continue;
    }

    const items = groups.get(definition.group) ?? [];
    items.push(definition);
    groups.set(definition.group, items);
  }

  return [...groups.entries()]
    .sort((left, right) => left[0].localeCompare(right[0]))
    .map(([group, items]) => ({
      group,
      items: items.sort((left, right) => left.label.localeCompare(right.label))
    }));
}

function hasPermission(roleId: EntityId, permission: PermissionKind): boolean {
  return selectedPermissions[roleId]?.has(permission) === true;
}

function setPermission(roleId: EntityId, permission: PermissionKind, selected: boolean): void {
  selectedPermissions[roleId] ??= new Set<PermissionKind>();
  if (selected) {
    selectedPermissions[roleId].add(permission);
  } else {
    selectedPermissions[roleId].delete(permission);
  }
}

function assignablePermissions(role: RoleSummary, group: PermissionGroup): PermissionKind[] {
  return group.items
    .map(definition => definition.permission)
    .filter(permission => !isLockedPermission(role, permission));
}

function groupAllSelected(roleId: EntityId, group: PermissionGroup): boolean {
  const role = roles.value?.roles.find(candidate => candidate.id === roleId);
  if (!role) {
    return false;
  }

  const assignable = assignablePermissions(role, group);
  return assignable.length > 0 && assignable.every(permission => hasPermission(roleId, permission));
}

function toggleGroup(roleId: EntityId, group: PermissionGroup): void {
  const role = roles.value?.roles.find(candidate => candidate.id === roleId);
  if (!role) {
    return;
  }

  const select = !groupAllSelected(roleId, group);
  for (const permission of assignablePermissions(role, group)) {
    setPermission(roleId, permission, select);
  }
}

function roleDirtyCount(roleId: EntityId): number {
  const current = selectedPermissions[roleId] ?? new Set<PermissionKind>();
  const original = originalPermissions[roleId] ?? new Set<PermissionKind>();
  let count = 0;
  for (const permission of current) {
    if (!original.has(permission)) {
      count++;
    }
  }

  for (const permission of original) {
    if (!current.has(permission)) {
      count++;
    }
  }

  return count;
}

function discardChanges(): void {
  const roleId = selectedRoleId.value;
  if (roleId == null) {
    return;
  }

  selectedPermissions[roleId] = new Set(originalPermissions[roleId] ?? new Set<PermissionKind>());
}

async function saveRole(): Promise<void> {
  const roleId = selectedRoleId.value;
  if (roleId == null) {
    return;
  }

  try {
    const role = await CommandsService.commandUpdateRolePermissionsCommand({
      requestBody: {
        roleId,
        permissions: [...(selectedPermissions[roleId] ?? new Set<PermissionKind>())].sort()
      }
    });
    replaceRole(role);
    selectedPermissions[role.id] = new Set(role.permissions ?? []);
    originalPermissions[role.id] = new Set(role.permissions ?? []);
    message.value = 'Role saved.';
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to save role.');
  }
}

async function startCreate(): Promise<void> {
  creating.value = true;
  newRoleName.value = '';
  newRoleScope.value = RoleScope.PROJECT;
  await nextTick();
  createInput.value?.focus();
}

function cancelCreate(): void {
  creating.value = false;
  newRoleName.value = '';
}

async function createRole(): Promise<void> {
  const name = newRoleName.value.trim();
  if (!name) {
    return;
  }

  const scope = newRoleScope.value;
  try {
    const role = await CommandsService.commandCreateRoleCommand({ requestBody: { name, scope } });
    roles.value ??= { roles: [], permissionDefinitions: [] };
    roles.value.roles.push({ ...role, permissions: [] });
    roles.value.roles.sort((left, right) =>
      left.scope === right.scope
        ? left.name.localeCompare(right.name)
        : left.scope.localeCompare(right.scope));
    selectedPermissions[role.id] = new Set<PermissionKind>();
    originalPermissions[role.id] = new Set<PermissionKind>();
    creating.value = false;
    newRoleName.value = '';
    selectedRoleId.value = role.id;
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to create role.');
  }
}

function replaceRole(role: RoleSummary): void {
  if (roles.value == null) {
    throw new Error('Roles must be loaded before replacing a role.');
  }

  const index = roles.value.roles.findIndex(candidate => candidate.id === role.id);
  if (index < 0) {
    roles.value.roles.push(role);
    return;
  }

  roles.value.roles[index] = role;
}

function isLockedPermission(role: RoleSummary, permission: PermissionKind): boolean {
  return role.builtInKey === BuiltInRoleKey.ADMINISTRATOR && permission === PermissionKind.ADMIN_ACCESS;
}
</script>
